"use client"

import { useState, useEffect } from "react"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import { useAuthStore, useIsAdmin, useIsModerator } from "@/lib/auth-store"
import { LoginPromptDialog } from "./login-prompt-dialog"
import { NotificationsModal } from "@/components/notifications-modal"
import { useGlymUnread } from "@/hooks/use-glym-unread"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { AvatarWithBadge } from "@/components/badges/AvatarWithBadge"
import { User, Settings, Shield, LogOut, LogIn, TrendingUp, Bell, Briefcase, Globe, Rocket, Store, Map, Award, Coins, Gift, Heart, ShoppingBag, UserRound } from "@/lib/icons"
import { formatNumber } from "@/lib/utils"
import { FOUNDER_BADGE_CLASSES, FOUNDER_PLUS_BADGE_CLASSES, normalizeRoleTag, roleTagClasses, roleTagLabel } from "@/lib/creator-role-tags"
import { CurrencySelector } from "@/components/currency-selector"
import { LanguageSelector } from "@/components/language-selector"
import { SupportOnPatreonButton } from "@/components/patreon/patreon-support"
import { usePatreonMode } from "@/lib/use-patreon-mode"

// Which creator tags have a translated label under the "creatorTags"
// namespace. A tag missing from this set falls back to its raw value rather
// than asking next-intl for a key that doesn't exist (which would throw).
const TRANSLATED_TAGS = new Set([
  "scriptor",
  "artist_2d",
  "artist_3d",
  "va",
  "sd",
  "editor",
  "developer",
  "creator",
])

// Role colours live in lib/creator-role-tags.ts, shared with the profile/storefront role pill.

const SUBSCRIPTION_TIER_LABELS: Record<string, string> = {
  supporter: "Supporter",
  goon: "Goon",
  goon_plus: "Goon+",
  goon_max: "Goon Max",
}

// Exact hex on purpose: the theme remaps Tailwind blue/violet/purple onto the brand purple,
// which made these tiers indistinguishable. Founder / Founder+ have their own gradients
// (FOUNDER_BADGE_CLASSES / FOUNDER_PLUS_BADGE_CLASSES) and are not in this table.
const SUBSCRIPTION_TIER_COLORS: Record<string, string> = {
  supporter: "bg-[#3b82f6] text-white border-[#2563eb]",
  goon: "bg-primary text-primary-foreground border-primary",
  goon_plus: "bg-[#fa8072] text-black border-[#e9695a]",
  goon_max: "bg-[#e11d1d] text-white border-[#b91414]",
}

interface Notification {
  id: string
  message: string
  type: string
  read_at: string | null
  created_at: string
  _source?: string
  companion?: { id?: string; name: string; avatar_url: string | null }
  companion_id?: string
  data?: Record<string, unknown>
}

export function UserMenu() {
  const router = useRouter()
  const t = useTranslations("userMenu")
  // Patreon mode (docs/PATREON_PLAN_C.md): while there is no payment processor, the upgrade button becomes "Support on Patreon".
  const { enabled: patreonMode } = usePatreonMode()
  const tTags = useTranslations("creatorTags")
  const [showLogin, setShowLogin] = useState(false)
  const [loginMode, setLoginMode] = useState<"login" | "forgot-password">("login")
  const [showNotifications, setShowNotifications] = useState(false)
  const { user, profile, isGuest, isLoading, signOut } = useAuthStore()

  // Auto-open forgot-password dialog when redirected from expired reset link
  useEffect(() => {
    if (typeof window === "undefined") return
    const params = new URLSearchParams(window.location.search)
    if (params.get("reset") === "1") {
      setLoginMode("forgot-password")
      setShowLogin(true)
      // Clean the param from the URL without a re-render
      window.history.replaceState({}, "", window.location.pathname)
    }
  }, [])
  const isAdmin = useIsAdmin()
  const isModerator = useIsModerator()

  const isCreator = profile?.creator_mode === true
  const creatorTags = profile?.creator_tags || []

  // Notifications — just the unread count for the badge; the list itself now only lives in
  // NotificationsModal (opened from the "Notifications" nav row), not previewed inline here.
  const [unreadCount, setUnreadCount] = useState(0)
  const [aiCredits, setAiCredits] = useState(0)
  const [goopBalance, setGoopBalance] = useState(0)
  const [gooncoinBalance, setGooncoinBalance] = useState(0)
  const [ordersUnread, setOrdersUnread] = useState(0)
  const [storehubUnread, setStorehubUnread] = useState(0)
  // Glym chat badge — app-wide announcements from her (partnerships etc.) plus
  // anything she's said since the user last opened the chat. Own hook rather
  // than another fetch in the effect below because the same count also renders
  // on the home page's floating Glym button.
  const glymUnread = useGlymUnread()

  useEffect(() => {
    if (isGuest || !user) return

    const fetchUnreadCount = async () => {
      try {
        const res = await fetch("/api/notifications?limit=5")
        const data = await res.json()
        if (data.notifications) {
          setUnreadCount(data.notifications.filter((n: Notification) => !n.read_at).length)
        }
      } catch {}
    }

    fetchUnreadCount()
    const interval = setInterval(fetchUnreadCount, 30000)
    return () => clearInterval(interval)
  }, [user, isGuest])

  useEffect(() => {
    if (isGuest || !user) return

    fetch("/api/account/balances")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) {
          setAiCredits(data.aiCredits)
          setGoopBalance(data.goopBalance)
          setGooncoinBalance(data.gooncoinBalance)
        }
      })
      .catch(() => {})
  }, [user, isGuest])

  // "My Orders" / "Storehub" badges — reuses the exact same per-order "unread" logic
  // and comm_viewed_* localStorage keys that /commissions/my-commissions and Storehub's
  // own order list already use (rather than a separate, message-only server check),
  // so a badge here means the same thing it means on those pages — new messages on
  // the client side, and both new messages AND un-viewed WIP uploads on the client side
  // specifically (WIPs are always creator-authored, so they don't count as "unread" for
  // the creator's own badge).
  useEffect(() => {
    if (isGuest || !user) return

    const fetchOrdersUnread = async () => {
      try {
        let lastViewedMap: Record<string, string> = {}
        try {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)
            if (key?.startsWith("comm_viewed_")) {
              lastViewedMap[key.replace("comm_viewed_", "")] = localStorage.getItem(key)!
            }
          }
        } catch {}

        const res = await fetch("/api/commissions")
        if (res.ok) {
          const data = await res.json()
          const commissions: any[] = data.commissions ?? []

          const asClient = commissions.filter(
            (c) => c.client_id === user.id || (c.client_id === null && c.artist_id !== user.id)
          )
          const clientUnread = asClient.filter((c) => {
            const lastActivity = [c.last_message_at, c.last_wip_at].filter(Boolean).sort().at(-1)
            if (!lastActivity) return false
            const lastViewed = lastViewedMap[c.id]
            return !lastViewed || new Date(lastActivity) > new Date(lastViewed)
          }).length
          setOrdersUnread(clientUnread)

          const asCreator = commissions.filter((c) => c.artist_id === user.id)
          const creatorUnread = asCreator.filter((c) => {
            if (!c.last_message_at) return false
            const lastViewed = lastViewedMap[c.id]
            return !lastViewed || new Date(c.last_message_at) > new Date(lastViewed)
          }).length
          setStorehubUnread(creatorUnread)
        }
      } catch {}
    }

    fetchOrdersUnread()
    const interval = setInterval(fetchOrdersUnread, 30000)
    return () => clearInterval(interval)
  }, [user, isGuest])

  if (isLoading) {
    return (
      <Button variant="ghost" size="icon" disabled>
        <User className="h-5 w-5" />
      </Button>
    )
  }

  if (isGuest || !user) {
    return (
      <>
        {/* On narrow screens the toolbar is crowded, so Log In collapses to a round, softly purple-tinted icon button. */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowLogin(true)}
          aria-label={t("logIn")}
          title={t("logIn")}
          className="text-foreground dark:text-foreground border-border hover:bg-accent hover:text-accent-foreground font-medium max-[500px]:h-7 max-[500px]:w-7 max-[500px]:rounded-full max-[500px]:border-primary/40 max-[500px]:bg-primary/15 max-[500px]:p-0 max-[500px]:text-primary"
        >
          <LogIn className="mr-2 h-4 w-4 max-[500px]:mr-0" />
          <span className="max-[500px]:hidden">{t("logIn")}</span>
        </Button>
        <LoginPromptDialog open={showLogin} onOpenChange={setShowLogin} feature="your account" initialMode={loginMode} />
      </>
    )
  }

  const initials = profile?.username?.slice(0, 2).toUpperCase() || user.email?.slice(0, 2).toUpperCase() || "U"

  return (
    <>
    <NotificationsModal
      open={showNotifications}
      onOpenChange={setShowNotifications}
      onUnreadCountChange={setUnreadCount}
    />
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative max-[390px]:h-6 max-[390px]:w-6">
          <AvatarWithBadge
            src={profile?.profile_picture_url}
            alt={profile?.username || ""}
            size={32}
            userId={profile?.id}
            noBadge
          />
          {/* Unread notification badge */}
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 w-4 max-[390px]:h-3 max-[390px]:w-3 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-background">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
          {/* Verified/creator dot (only show if no notification badge) */}
          {unreadCount === 0 && profile?.is_verified && (
            <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-green-500 border-2 border-background" />
          )}
          {unreadCount === 0 && isCreator && !profile?.is_verified && (
            <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-primary border-2 border-background" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {/* User info */}
        <div className="px-3 py-2">
          <div className="flex items-center gap-2.5">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="font-semibold text-sm truncate">{profile?.display_name || profile?.username || t("fallbackUserName")}</p>
                {/* Founder+ — a Founder who ALSO carries an active paid
                    subscription. Founder is gold; Founder+ is gold shifting to
                    violet, so the two read as different tiers at a glance
                    (see lib/creator-role-tags.ts). */}
                {profile?.is_founder ? (
                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 font-semibold shrink-0 ${profile.subscription_tier && profile.subscription_tier !== "free" ? FOUNDER_PLUS_BADGE_CLASSES : FOUNDER_BADGE_CLASSES}`}>
                    {profile.subscription_tier && profile.subscription_tier !== "free"
                      ? `${t("founder")}+`
                      : t("founder")}
                  </Badge>
                ) : profile?.subscription_tier && profile.subscription_tier !== "free" && SUBSCRIPTION_TIER_LABELS[profile.subscription_tier] ? (
                  <Badge
                    variant="outline"
                    className={`text-[10px] px-1.5 py-0 h-4 font-medium shrink-0 ${SUBSCRIPTION_TIER_COLORS[profile.subscription_tier] || ""}`}
                  >
                    {SUBSCRIPTION_TIER_LABELS[profile.subscription_tier]}
                  </Badge>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
          </div>
          {creatorTags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {creatorTags.map((tag) => (
                <Badge
                  key={tag}
                  variant="outline"
                  className={`text-[10px] px-1.5 py-0 h-4 font-medium ${roleTagClasses(tag)}`}
                >
                  {TRANSLATED_TAGS.has(normalizeRoleTag(tag)) ? tTags(normalizeRoleTag(tag)) : roleTagLabel(tag)}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Subscription CTA — wording follows current tier so there's always a way
            back to /subscribe, whether that means starting, upgrading, or downgrading.
            Founders already have every perk (nothing to upgrade to, and no reason to
            downgrade a one-time purchase), but the entry point itself shouldn't
            disappear — they can still go show extra support, so it just relabels
            instead of vanishing (was previously hidden outright for founders, which
            contradicted this very comment). */}
        <div className="px-2 pb-2">
          {patreonMode ? (
            <SupportOnPatreonButton href="/subscribe/patreon" />
          ) : (
          <button
            onClick={() => router.push("/subscribe")}
            style={{ background: "linear-gradient(135deg, #5b21b6 0%, #7c3aed 50%, #6d28d9 100%)", boxShadow: "0 4px 16px rgba(109,40,217,0.5)" }}
            className="w-full flex items-center justify-center gap-2 rounded-xl py-3 px-3 text-sm font-bold text-white hover:opacity-95 hover:scale-[1.01] active:scale-[0.99] transition-all"
          >
            <Rocket className="h-4 w-4 fill-yellow-300 text-yellow-300 shrink-0" />
            <span className="text-white drop-shadow-sm">
              {profile?.is_founder
                ? t("supportGoonru")
                : !profile?.subscription_tier || profile.subscription_tier === "free"
                ? t("supportGoonru")
                : profile.subscription_tier === "goon_max"
                ? t("downgrade")
                : t("upgrade")}
            </span>
          </button>
          )}
        </div>

        {/* Live balances — always-reachable place to check both currencies */}
        <div className="px-2 pb-2">
          <button
            onClick={() => router.push("/wallet")}
            className="w-full flex items-center justify-center gap-3 rounded-lg py-2 px-3 text-sm hover:bg-accent transition-colors border"
          >
            <span className="flex items-center gap-1.5">
              <Coins className="h-3.5 w-3.5 text-amber-400" />
              <span className="font-semibold">{formatNumber(aiCredits)}</span>
            </span>
            <span className="h-4 w-px bg-border" />
            <span className="flex items-center gap-1.5">
              <Image src="/goop/goop_t1.webp" alt={t("goop")} width={22} height={22} className="rounded" />
              <span className="font-semibold">{formatNumber(goopBalance)}</span>
            </span>
            <span className="h-4 w-px bg-border" />
            <span className="flex items-center gap-1.5">
              <Image src="/gooncoin/gooncoin_t1.webp" alt={t("gooncoin")} width={22} height={22} className="rounded" />
              <span className="font-semibold">{formatNumber(gooncoinBalance)}</span>
            </span>
          </button>
        </div>

        <DropdownMenuSeparator />

        {/* 2-column nav grid */}
        <div className="grid grid-cols-2 gap-0.5 px-1.5 pb-1">
          {[
            { id: "notifications", icon: Bell, label: t("notifications"), onClick: () => setShowNotifications(true), badge: unreadCount },
            { id: "profile", icon: User, label: t("profile"), href: "/profile" },
            { id: "settings", icon: Settings, label: t("settings"), href: "/settings" },
            { id: "wishlist", icon: Heart, label: t("wishlist"), href: "/wishlist" },
            { id: "myOrders", icon: ShoppingBag, label: t("myOrders"), href: "/commissions/my-commissions", badge: ordersUnread },
            { id: "glym", icon: UserRound, label: t("glym"), href: "/glym", badge: glymUnread },
          ].map(({ id, icon: Icon, label, href, onClick, badge }) => (
            <button
              key={id}
              onClick={onClick ?? (() => router.push(href!))}
              className="w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors text-left"
            >
              <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="truncate flex-1">{label}</span>
              {!!badge && (
                <Badge variant="destructive" className="h-4 px-1.5 text-[10px] shrink-0">
                  {badge > 9 ? "9+" : badge}
                </Badge>
              )}
            </button>
          ))}
        </div>

        {/* Creator section */}
        {isCreator ? (
          <>
            <DropdownMenuSeparator />
            <div className="px-1.5 pb-1">
              <p className="text-[10px] text-muted-foreground px-2 py-1 uppercase tracking-wider">{t("creatorSectionLabel")}</p>
              {/* One entry — Storehub is no longer a separate page, it's Artist
                  mode inside the Creator Hub. The badge still counts unread
                  commission activity so incoming work stays visible here. */}
              <button
                onClick={() => router.push("/creator/dashboard")}
                className="w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-primary font-medium hover:bg-accent transition-colors text-left"
              >
                <TrendingUp className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate flex-1">{t("creatorHub")}</span>
                {storehubUnread > 0 && (
                  <Badge variant="destructive" className="h-4 px-1.5 text-[10px] shrink-0">
                    {storehubUnread > 9 ? "9+" : storehubUnread}
                  </Badge>
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="px-1.5 pb-1">
            <button
              onClick={() => router.push("/settings?tab=account#creator")}
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <TrendingUp className="h-3 w-3 shrink-0" />
              {t("enableCreatorMode")}
            </button>
          </div>
        )}

        {(isAdmin || isModerator) && (
          <>
            <DropdownMenuSeparator />
            <div className="px-1.5 pb-1">
              <button
                onClick={() => router.push("/admin")}
                className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <Shield className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                {t("adminDashboard")}
              </button>
            </div>
          </>
        )}

        <DropdownMenuSeparator />
        <div className="px-1.5 pb-0.5 space-y-0.5">
          <button
            onClick={() => router.push("/referrals")}
            className="w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors text-left"
          >
            <Gift className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="truncate">{t("referrals")}</span>
          </button>
          <div className="flex items-center gap-2 rounded-md px-2.5 py-2">
            <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <CurrencySelector className="h-7 w-[58px] text-xs" />
            <LanguageSelector className="h-7 flex-1 text-xs" />
          </div>
        </div>
        <DropdownMenuSeparator />
        <div className="px-1.5 pb-1.5 pt-0.5 space-y-0.5">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => router.push("/roadmap")}
              className="flex flex-1 items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <Map className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              {t("roadmap")}
            </button>
            <button
              onClick={() => router.push("/achievements")}
              className="flex flex-1 items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <Award className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              {t("achievements")}
            </button>
          </div>
          <button
            onClick={() => signOut()}
            className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5 shrink-0" />
            {t("logOut")}
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
    </>
  )
}
